/**
 * @fileoverview The one place undo storage lives.
 *
 * A command that can be undone declares three functions on its UserCmd registration:
 *
 *     capture(...args)   -> snapshot   what the call could change, recorded before it runs.
 *                                      May throw to refuse the call before anything happens.
 *     apply(...args)     -> result     the command itself, exactly as it would be written
 *                                      without undo: the caller's own arguments, its own
 *                                      validation. It never sees the snapshot.
 *     restore(snapshot)                put the captured thing back, completely -- including any
 *                                      repaint. Undo and rollback call it the same way.
 *
 * capture and apply may each work out the same thing from the arguments (which atoms a
 * selection names, say). That duplication is deliberate: it keeps apply readable as the command
 * it is, and the two run back to back, so they agree.
 *
 * UserCmd hands them here, and undoable() composes the doFn/undoFn pair that UserAction runs.
 * This module owns two things the commands never see:
 *
 *   1. Rollback. If apply throws (or rejects), the snapshot is restored and the ORIGINAL error
 *      rethrown. A style change that fails half-way is put back, not left half-applied. Restore
 *      repaints on rollback too: the caller's own repaint, typically the next statement, is
 *      skipped by the exception, so a rollback that left repainting to the caller would leave a
 *      canvas that no longer matches the model. If restore itself throws during a rollback, that
 *      failure is logged and the original error is still the one rethrown -- a second failure
 *      inside the recovery must not hide the first.
 *   2. Where the snapshot is kept between do and undo. Today that is a stack per command. The
 *      queued UserAction knows nothing about it, which is why moving the snapshot INTO the
 *      queued action later is a change here and in UserCmd.js only: every command already
 *      declares exactly what that design needs. See docs/gibbs-migration-register.md, Slice C
 *      item 4b.
 *
 * Why a stack entry is recorded before apply runs, not after it succeeds: UserAction.do() does
 * not await doFn, so an asynchronous command is already in the undo queue by the time its apply
 * settles. The stack must therefore hold one entry per queue entry from the moment the queue
 * does, in call order, or two overlapping calls of one command could settle out of order and
 * pair the wrong snapshot with the wrong undo. An entry whose apply failed stays on the stack
 * marked so, and undoing it is a no-op that keeps the two aligned. A synchronous throw is the
 * one exception: UserAction.do() never enqueues a call that threw, so its entry is removed (and
 * UserAction.redo() steps its index back for the same reason).
 *
 * Why a generation counter: ResetQueues() (the zapAll handler) clears every stack. A command
 * still in flight across that reset must neither restore a snapshot of objects that no longer
 * exist nor record a snapshot onto the cleared stack.
 */

import { Log } from 'BMapsSrc/Log';

const stacks = new Set();
let generation = 0;

/**
 * @param {object} spec
 * @param {string} spec.name - the command's name, for error messages
 * @param {(...args: any[]) => any} spec.capture
 * @param {(...args: any[]) => any} spec.apply
 * @param {(snapshot: any) => any} spec.restore
 * @returns {{
 *   doFn: (...args: any[]) => any,
 *   undoFn: () => any,
 *   capture: function, apply: function, restore: function,
 * }}
 */
export function undoable({
    name, capture, apply, restore,
}) {
    for (const [role, fn] of Object.entries({ capture, apply, restore })) {
        if (typeof fn !== 'function') {
            throw new TypeError(`undoable(${name}): ${role} must be a function.`);
        }
    }
    const stack = [];
    stacks.add(stack);

    const doFn = (...args) => {
        const snapshot = capture(...args);
        const startedIn = generation;
        const entry = { done: false, snapshot };
        stack.push(entry);

        const rollBack = (error) => {
            if (startedIn === generation) {
                try {
                    restore(snapshot);
                } catch (restoreError) {
                    Log.error(`${name}: rollback after a failed do also failed: ${restoreError}`);
                }
            }
            throw error;
        };

        let result;
        try {
            result = apply(...args);
        } catch (error) {
            // Not enqueued: UserAction.do() only reaches Enqueue when doFn returns. The entry can
            // already be gone if apply triggered a reset; then there is nothing to remove, and
            // splice(-1, 1) would remove someone else's entry.
            const index = stack.indexOf(entry);
            if (index >= 0) stack.splice(index, 1);
            rollBack(error);
        }

        if (result && typeof result.then === 'function') {
            // Enqueued already; the entry stays, and becomes undoable only if apply succeeds.
            return result.then((value) => {
                if (startedIn === generation) entry.done = true;
                return value;
            }, rollBack);
        }
        entry.done = true;
        return result;
    };

    const undoFn = () => {
        const entry = stack.pop();
        if (!entry || !entry.done) return undefined;
        return restore(entry.snapshot);
    };

    return {
        doFn, undoFn, capture, apply, restore,
    };
}

/** Forget every recorded snapshot. Called by UserAction.ResetQueues(), i.e. on zapAll. */
export function resetAll() {
    generation++;
    for (const stack of stacks) stack.length = 0;
}
